/**
 * A test's readable steps: `GET /workflows/:id` rendered to the same markdown
 * `cofactor workflow get --format markdown` prints.
 *
 * @module features/proofs/hooks/useWorkflowSteps
 */
import { useQuery } from '@tanstack/react-query';
import { workflowToSpec, type PullWorkflow } from '@chatsdet/canary-spec/browser';
import { toReaderMarkdown } from './specMarkdown';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';

type WorkflowDetail = PullWorkflow & { seedOutputsJson?: PullWorkflow['seedOutputs'] };

/** The flow as markdown; only needed for evidence that has no agent-written steps. */
export const useWorkflowSteps = (workflowId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['proof-workflow-steps', workflowId],
    enabled,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/workflows/${workflowId}`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load the test steps');
      const { workflow } = (await res.json()) as { workflow: WorkflowDetail };
      return {
        markdown: toReaderMarkdown(
          workflowToSpec({ ...workflow, seedOutputs: workflow.seedOutputsJson ?? workflow.seedOutputs })
        ),
      };
    },
  });
